// l2b-app-shell.js — top-level shell + router host.
//
// Mirrors KFE Web's <kfe-app-shell>, stripped to the scaffold's needs. Brief 011
// (single-lesson embed) reduces the header to the minimum the embed needs: the
// title + a "Learn English"/"Learn Mandarin" direction toggle. The back chevron
// and 🌐 re-onboarding affordance are gone — there is no lesson list to go back
// to. A <main> outlet hosts the routed screen; a version footer sits below.
//
// RE-MOUNT DISCIPLINE (carried over from KFE Web, the #1 risk): the shell must
// NOT re-render itself on route changes — that would replace the <main> outlet
// and unmount/remount every routed screen on every navigation. The back-chevron
// visibility is updated IN PLACE on `kfe:route-changed` / popstate (see
// _updatePath), never via a full render().
//
// Routes are registered once, on `kfe:ready` (dispatched by app.js after every
// custom element is defined).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { track } from "../core/analytics.js";
import * as router from "../router.js";
import * as lang from "../i18n/language-manager.js";
import { VERSION } from "../version.js";

export class L2BAppShell extends KFEComponent {
  static styles = `
    /* Fill the viewport so the grid pins the footer to the bottom on short
       pages. min-height:100dvh is self-contained — it does NOT depend on the
       html,body{height:100%} chain resolving (when that chain collapsed to
       'auto', the footer rode up under short content). dvh tracks the iOS
       URL-bar so the footer isn't hidden behind it. The middle row is
       minmax(0,1fr), NOT 1fr: the 0 minimum lets <main> shrink below its
       content so tall pages scroll INSIDE main (overflow:auto) with the footer
       still pinned, instead of the row blowing out to content height and the
       footer floating. NB index.html must not set display/min-height on the
       light-DOM l2b-app-shell element — an outer-document rule on the host
       overrides these :host declarations in Chrome regardless of specificity. */
    :host { display: grid; grid-template-rows: auto minmax(0, 1fr) auto; min-height: 100dvh; }
    /* Slim embed header (Brief 011): just the title + the direction toggle, over
       the solid SpeechGenie-blue field. No back chevron, no 🌐 re-onboarding
       affordance — there is nowhere to go "back" to (single lesson). */
    header {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: var(--l2b-bg-header);
      -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
      border-bottom: 1px solid var(--l2b-border);
    }
    header h1 {
      margin: 0; font-size: var(--l2b-h1); color: var(--l2b-fg);
      flex: 1; min-width: 0; font-weight: 800; letter-spacing: 0.01em;
      text-shadow: 0 1px 3px rgba(0,0,0,0.6);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    /* Direction toggle (Brief 011): flips learning-Mandarin ⇄ learning-English in
       place and re-labels to offer the reverse. A clear white pill that reads on
       the blue field; replaces the whole onboarding flow. */
    button.direction {
      flex: none; cursor: pointer; white-space: nowrap;
      padding: 0.4rem 0.9rem; border-radius: 999px;
      border: 1px solid var(--l2b-border);
      background: rgba(255, 255, 255, 0.92); color: var(--sg-blue, #153e63);
      font-weight: 700; font-size: 0.95rem; line-height: 1;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    }
    button.direction:hover { background: #ffffff; }
    button.direction:active { transform: translateY(1px); }
    /* Brief 013 — header fit at ~360px. h1 already shrinks (flex:1, min-width:0,
       ellipsis) and the toggle is flex:none so it's never clipped off the right,
       but on the narrowest phones tighten the gap/padding and toggle font so the
       title keeps a readable width before ellipsising. Desktop untouched. */
    @media (max-width: 360px) {
      header { gap: 0.4rem; padding: 0.5rem 0.6rem; }
      button.direction { padding: 0.35rem 0.7rem; font-size: 0.85rem; }
    }
    /* Transparent so the blue body shows through; screens supply their own panels. */
    main { overflow: auto; padding: 1rem; background: transparent; color: var(--l2b-fg); }
    /* Per-screen dissolve: the routed element is the only child of <main>, swapped
       on navigation — so this fires once per screen entry, not on in-screen
       re-renders. A simplified stand-in for the Unity dissolve shader (Gotcha 3). */
    main > * { animation: l2b-screen-in 0.45s ease both; }
    @keyframes l2b-screen-in {
      from { opacity: 0; transform: translateY(8px) scale(0.992); filter: blur(2px); }
      to   { opacity: 1; transform: none; filter: none; }
    }
    @media (prefers-reduced-motion: reduce) { main > * { animation: none; } }
    footer {
      padding: 0.5rem 1rem; font-size: 0.75rem; color: var(--l2b-muted);
      background: var(--l2b-bg-header);
      -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
      border-top: 1px solid var(--l2b-border); text-align: center;
    }
  `;

  onConnected() {
    // Register routes once, after app.js has defined every element. Declaring
    // them here (not at module load) mirrors KFE Web's <kfe-app-shell>. Brief 011:
    // only the exercise (Lesson 1) is reachable — the "/" onboarding and
    // "/dashboard" routes are gone; app.js redirects any stray path to Lesson 1.
    document.addEventListener("kfe:ready", () => {
      router.start([
        { path: "/exercise/:itemId", tag: "l2b-exercise", props: (p) => ({ itemId: p.itemId }) },
        { path: "/test/:itemId",     tag: "l2b-test-exercise", props: (p) => ({ itemId: p.itemId }) },
      ], /** @type {HTMLElement} */ (this.shadowRoot?.querySelector("main")));
    }, { once: true });

    // The shell renders synchronously (on upgrade) before initLanguages()
    // resolves, so the header can briefly show raw keys. Re-render once the i18n
    // pack arrives — and because re-rendering replaces the <main> outlet, re-mount
    // the current route into the fresh outlet (mirrors KFE's <kfe-app-shell>).
    // The direction toggle relies on this same path: flipping the L1 fires
    // kfe:i18n, which re-labels the header AND remounts the exercise — restarting
    // Lesson 1 in the new direction (Success criterion 3).
    const onI18n = () => {
      this._scheduleRender();
      queueMicrotask(() => {
        const main = /** @type {HTMLElement|null} */ (this.shadowRoot?.querySelector("main"));
        if (main) router.remount(main);
      });
    };
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));
  }

  /**
   * The direction-toggle label: it always offers the OTHER direction. Learning
   * Mandarin (target = zh) → "Learn English"; learning English (target = en) →
   * "Learn Mandarin" (rendered in the active L1 pack, so it reads 学普通话 once
   * flipped). Reuses the onboarding i18n keys.
   * @returns {string}
   */
  _directionLabel() {
    return lang.current().targetLanguage === "zh"
      ? t("onboarding.learn.en")
      : t("onboarding.learn.zh");
  }

  // Flip the learning direction in place (Brief 011 / Success criterion 3).
  // Swaps BOTH axes (target ⇄ L1) via the Brief 004b bidirectional setters and
  // persists. setMotherTongue → setLanguage fires kfe:i18n, which re-labels this
  // header and remounts the exercise (restarting Lesson 1 in the new direction).
  // Set target FIRST (sync) so the remount sees both axes already flipped.
  _onToggleDirection = async () => {
    if (lang.current().targetLanguage === "zh") {
      lang.setTarget("en");          // learn English: English stimulus, ⓘ → Mandarin
      await lang.setMotherTongue("zh");
    } else {
      lang.setTarget("zh");          // learn Mandarin: Mandarin stimulus, ⓘ → English
      await lang.setMotherTongue("en");
    }
    // Brief 016: which direction the audience actually wants. Fired AFTER the
    // switch, so `to_language` and the common `target_language` param agree.
    track("language_toggle", { to_language: lang.current().targetLanguage });
  };

  render() {
    return html`
      <header>
        <h1>${t("app.title")}</h1>
        <button class="direction" type="button" on:click=${this._onToggleDirection}>
          ${this._directionLabel()}
        </button>
      </header>
      <main></main>
      <footer>L2B web · v${VERSION}</footer>
    `;
  }
}
